import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { XCircle, UploadSimple, Check, Warning, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * Phase 13 Beat 1 · Bulk Import dialog.
 *
 * Feb 2026 UAT — the previous single-shot commit endpoint routinely tripped
 * the 120s proxy timeout because every URL calls Claude for JD extraction.
 * This dialog now:
 *   1. POSTs the URL list → backend queues a `bulk_import_jobs` row and
 *      returns `{ job_id, total }` immediately.
 *   2. Polls `GET /jobs/bulk-import/jobs/{job_id}` every 1.5s and renders a
 *      progress bar with live created / duplicates / failed counters.
 *   3. When `status === "complete"`, unlocks the "Done" button so the
 *      Executive can refresh the workspace.
 */
export default function BulkJobImportDialog({ onClose, onDone }) {
  const [step, setStep] = useState("input"); // input → preview → progress
  const [urlsText, setUrlsText] = useState("");
  const [previewRows, setPreviewRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);       // { id, total, processed, ... }
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const parsedUrls = () =>
    urlsText.split(/[\r\n,]+/).map((s) => s.trim()).filter(Boolean).slice(0, 100);

  const runPreview = async () => {
    const urls = parsedUrls();
    if (urls.length === 0) { toast.error("Paste at least one URL."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk-import/preview", { urls });
      setPreviewRows(data.rows || []);
      setStep("preview");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Preview failed");
    } finally { setBusy(false); }
  };

  const startCommit = async () => {
    const urls = previewRows.map((r) => r.url);
    setBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk-import/commit", { urls });
      // Seed the progress view with the queue snapshot.
      setJob({ id: data.job_id, total: data.total, processed: 0,
                created: 0, duplicates: 0, failed: [], results: [],
                status: data.status || "queued" });
      setStep("progress");
      toast.success(`Import started — ${data.total} URL(s) queued.`);
      // Poll every 1.5s. Each URL takes ~5-15s to extract, so this rate is
      // gentle on the server while still feeling live.
      pollRef.current = setInterval(async () => {
        try {
          const { data: snap } = await api.get(`/jobs/bulk-import/jobs/${data.job_id}`);
          setJob(snap);
          if (snap.status === "complete") {
            clearInterval(pollRef.current); pollRef.current = null;
          }
        } catch {
          // Transient poll failure — keep trying; the interval will retry.
        }
      }, 1500);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Import failed to start");
    } finally { setBusy(false); }
  };

  const onExcelFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target.result || "");
      const urls = text.match(/https?:\/\/[^\s,;"']+/g) || [];
      if (urls.length === 0) { toast.error("No URLs found in the file."); return; }
      setUrlsText(urls.join("\n"));
      toast.success(`${urls.length} URL(s) parsed — click Validate to preview.`);
    };
    reader.readAsText(f);
  };

  const pct = job && job.total ? Math.round((job.processed / job.total) * 100) : 0;
  const done = job?.status === "complete";

  return (
    <div className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
         data-testid="bulk-import-dialog">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl">
        <div className="p-6 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">Bulk Import Jobs</h3>
            <p className="text-xs text-slate-500 mt-1">
              Paste up to 100 URLs (one per line) or upload a CSV/TSV/TXT file with URLs.
              Import runs in the background — you can leave this dialog open to watch progress.
            </p>
          </div>
          <button onClick={onClose} data-testid="bulk-import-close" className="text-slate-400 hover:text-slate-700">
            <XCircle size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {step === "input" && (
            <>
              <textarea value={urlsText} onChange={(e) => setUrlsText(e.target.value)}
                        rows={8}
                        data-testid="bulk-urls-textarea"
                        placeholder="https://abc.com/job/123&#10;https://xyz.com/job/456&#10;https://def.com/job/789"
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono" />
              <div className="flex items-center justify-between">
                <label className="text-xs inline-flex items-center gap-2 text-slate-600 cursor-pointer"
                       data-testid="bulk-file-label">
                  <UploadSimple size={13} /> Upload CSV/TSV/TXT file
                  <input type="file" accept=".csv,.tsv,.txt"
                         onChange={onExcelFile}
                         data-testid="bulk-file-input"
                         className="hidden" />
                </label>
                <button onClick={runPreview} disabled={busy || !urlsText.trim()}
                        data-testid="bulk-preview-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                  {busy ? "Validating…" : "Validate URLs"}
                </button>
              </div>
            </>
          )}

          {step === "preview" && (
            <div className="space-y-3" data-testid="bulk-preview-list">
              <div className="text-xs text-slate-500">
                {previewRows.length} URL(s) validated. Duplicates will reuse the existing job.
              </div>
              <div className="max-h-72 overflow-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                {previewRows.map((r, i) => (
                  <div key={r.url || `row-${i}`} className="p-3 flex items-start gap-3 text-xs" data-testid={`bulk-preview-row-${i}`}>
                    <div className="flex-1">
                      <div className="font-mono text-slate-700 break-all">{r.url}</div>
                      {r.duplicate && r.duplicate_job && (
                        <div className="mt-1 text-indigo-700 inline-flex items-center gap-1">
                          <Warning size={11} /> Duplicate of <b>{r.duplicate_job.job_title}</b> · {r.duplicate_job.company}
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium
                                      ${r.duplicate ? "bg-indigo-50 border-indigo-200 text-indigo-800"
                                                    : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                      {r.duplicate ? "Duplicate" : "New"}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <button onClick={() => setStep("input")} className="text-xs text-slate-500 hover:text-slate-700 underline">
                  Back
                </button>
                <button onClick={startCommit} disabled={busy}
                        data-testid="bulk-commit-btn"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium py-2 px-4 disabled:opacity-50">
                  {busy ? "Starting…" : "Confirm & Import"}
                </button>
              </div>
            </div>
          )}

          {step === "progress" && job && (
            <div className="space-y-4 text-sm" data-testid="bulk-progress">
              <div className={`border rounded-lg p-3 flex items-center gap-2 ${
                done ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                     : "border-sky-200 bg-sky-50 text-sky-800"}`}
                   data-testid="bulk-progress-banner">
                {done ? <Check size={14} /> : <ArrowsClockwise size={14} className="animate-spin" />}
                <span>
                  {done
                    ? `Import complete — ${job.processed} of ${job.total} URL(s) processed.`
                    : `Importing in background… ${job.processed} of ${job.total} URL(s) done (${pct}%).`}
                </span>
              </div>

              {/* Progress bar */}
              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-testid="bulk-progress-bar">
                <div className={`h-full transition-all duration-500 ${done ? "bg-emerald-500" : "bg-sky-500"}`}
                     style={{ width: `${pct}%` }} />
              </div>

              <div className="grid grid-cols-3 gap-3 text-center">
                <Counter label="New" value={job.created || 0} tone="emerald" testid="bulk-count-created" />
                <Counter label="Duplicates" value={job.duplicates || 0} tone="indigo" testid="bulk-count-duplicates" />
                <Counter label="Failed" value={(job.failed || []).length} tone="rose" testid="bulk-count-failed" />
              </div>

              {(job.failed || []).length > 0 && (
                <div className="text-xs text-rose-700 border border-rose-200 bg-rose-50 rounded-lg p-3">
                  <div className="font-medium mb-1">Failures</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {job.failed.slice(0, 8).map((f, i) => (
                      <li key={f.url || `fail-${i}`}><span className="font-mono">{f.url}</span> — {f.error}</li>
                    ))}
                    {job.failed.length > 8 && <li className="italic">…and {job.failed.length - 8} more</li>}
                  </ul>
                </div>
              )}

              <div className="flex justify-between items-center">
                <div className="text-[11px] text-slate-500">
                  {done
                    ? "You can safely close this dialog."
                    : "You can close this dialog — the import keeps running. Come back to see the outcome, or click Done when complete."}
                </div>
                <button onClick={onDone} disabled={!done}
                        data-testid="bulk-commit-done"
                        className="text-sm px-4 py-2 rounded bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 disabled:cursor-not-allowed">
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Counter({ label, value, tone, testid }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    indigo:  "bg-indigo-50 text-indigo-800 border-indigo-200",
    rose:    "bg-rose-50 text-rose-800 border-rose-200",
  };
  return (
    <div className={`rounded-lg border px-4 py-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-widest mt-0.5">{label}</div>
    </div>
  );
}
